"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StakePool = void 0;
const spl_token_1 = require("@solana/spl-token");
const accountParser_1 = require("../common/accountParser");
const web3_js_1 = require("@solana/web3.js");
const common_1 = require("../common");
const hooks_1 = require("../common/hooks");
class StakePool {
    /**
     * Creates an instance of StakePool.
     * @param {Program<StakeIdl>} program
     * @param {StakePoolAccounts} addresses
     * @param {StakePoolInfo} stakePool
     * @param {MintInfo} voteMint
     * @param {MintInfo} collateralMint
     * @param {JetTokenAccount} vault
     * @param {MintInfo} tokenMint
     * @memberof StakePool
     */
    constructor(program, addresses, stakePool, collateralMint, vault, tokenMint, maxVoterWeightRecord) {
        this.program = program;
        this.addresses = addresses;
        this.stakePool = stakePool;
        this.collateralMint = collateralMint;
        this.vault = vault;
        this.tokenMint = tokenMint;
        this.maxVoterWeightRecord = maxVoterWeightRecord;
    }
    /**
     * TODO:
     * @static
     * @param {Program<StakeIdl>} program
     * @param {string} seed
     * @returns {Promise<StakePool>}
     * @memberof StakePool
     */
    static async load(program, seed) {
        const addresses = this.deriveAccounts(program.programId, seed);
        const stakePool = (await program.account.stakePool.fetch(addresses.stakePool));
        const [collateralMintInfo, vaultInfo, tokenMintInfo] = await program.provider.connection.getMultipleAccountsInfo([
            addresses.stakeCollateralMint,
            addresses.stakePoolVault,
            stakePool.tokenMint
        ]);
        if (!collateralMintInfo) {
            throw new Error("Invalid collateral mint");
        }
        if (!vaultInfo) {
            throw new Error("Invalid stake vault");
        }
        if (!tokenMintInfo) {
            throw new Error("Invalid token mint");
        }
        const maxVoterWeightRecord = (await program.account.maxVoterWeightRecord.fetch(stakePool.maxVoterWeightRecord));
        const collateralMint = (0, accountParser_1.parseMintAccount)(collateralMintInfo.data, addresses.stakeCollateralMint);
        const vault = (0, accountParser_1.parseTokenAccount)(vaultInfo.data, addresses.stakePoolVault);
        const tokenMint = (0, accountParser_1.parseMintAccount)(tokenMintInfo === null || tokenMintInfo === void 0 ? void 0 : tokenMintInfo.data, stakePool.tokenMint);
        return new StakePool(program, addresses, stakePool, collateralMint, vault, tokenMint, maxVoterWeightRecord);
    }
    /**
     * TODO:
     * @static
     * @param {Program<StakeIdl>} [stakeProgram]
     * @returns {*}  {(StakePool | undefined)}
     * @memberof StakePool
     */
    static use(stakeProgram) {
        return hooks_1.Hooks.usePromise(async () => stakeProgram && StakePool.load(stakeProgram, StakePool.CANONICAL_SEED), [stakeProgram]);
    }
    /**
     * TODO:
     * @static
     * @param {PublicKey} programId
     * @param {string} seed
     * @returns {Promise<StakePoolAccounts>}
     * @memberof StakePool
     */
    static deriveAccounts(programId, seed) {
        const stakePool = (0, common_1.findDerivedAccount)(programId, seed);
        const stakeCollateralMint = (0, common_1.findDerivedAccount)(programId, seed, "collateral-mint");
        const stakePoolVault = (0, common_1.findDerivedAccount)(programId, seed, "vault");
        return {
            stakePool,
            stakeCollateralMint,
            stakePoolVault
        };
    }
    /**
     * Derive the MaxVoterWeightRecord for the realm
     *
     * @static
     * @param {PublicKey} programId
     * @param {PublicKey} realm
     * @return {PublicKey}
     * @memberof StakePool
     */
    static deriveMaxVoterWeightRecord(programId, realm) {
        return (0, common_1.findDerivedAccount)(programId, realm, "max-vote-weight-record");
    }
    /**
     * TODO:
     * @static
     * @param {Program<StakeIdl>} program
     * @param {CreateStakePoolParams} params
     * @returns {Promise<string>}
     * @memberof StakePool
     */
    static async withCreate(instructions, program, params) {
        const derivedAccounts = this.deriveAccounts(program.programId, params.args.seed);
        const maxVoterWeightRecord = this.deriveMaxVoterWeightRecord(program.programId, params.args.governanceRealm);
        const accounts = Object.assign(Object.assign(Object.assign({}, params.accounts), derivedAccounts), { maxVoterWeightRecord, tokenProgram: spl_token_1.TOKEN_PROGRAM_ID, systemProgram: web3_js_1.SystemProgram.programId, rent: web3_js_1.SYSVAR_RENT_PUBKEY });
        const ix = await program.methods
            .initPool(params.args.seed, {
            unbondPeriod: params.args.unbondPeriod,
            governanceRealm: params.args.governanceRealm
        })
            .accounts(accounts)
            .instruction();
        instructions.push(ix);
    }
}
exports.StakePool = StakePool;
/** The official Jet Stake Pool seed */
StakePool.CANONICAL_SEED = "bigmoose";
//# sourceMappingURL=stakePool.js.map